import { ReactNode } from "react";
import Loading from "../../Generic/loading";
import { parseEmsDate } from "../../Logic/emsDates";
import { isInboundDetail } from "../EmailDetailAPI";
import { EmailRef } from "../EmailSearchAPI";
import { DetailState, useEmailDetail } from "../useEmailDetail";
import ActionBar from "./ActionBar";
import { absent, clock, optionalText, STATUS_PILL, subjectMarker } from "./emailFields";
import EmlDownload from "./EmlDownload";
import Timeline from "./Timeline";

interface iEmailDetailProps {
    emailRef: EmailRef;
    tenantKey: string;
    /** mailboxID to display name, from the parent's GetMailboxHealth call. */
    mailboxNames: Map<number, string>;
    tokenFor: (tenantKey: string) => Promise<string>;
    /** `staleWorklist` is true when an action was taken (spec §4.4). */
    onBack: (staleWorklist: boolean) => void;
}

/**
 * Copy for the states that render an explanation rather than content.
 *
 * A `Record`, so a seventh `DetailState` cannot compile until it has copy —
 * the same guard `NOTICE` uses on the worklist. Sibling `state === "x" &&`
 * conditionals would render a header over nothing, with every test green.
 */
const NOTICE: Record<Exclude<DetailState, "loading" | "ready">, string> = {
    notFound:
        "This email is no longer available. It may have been purged since the worklist page you opened it from " +
        "was read.",
    // Elevation into the tenant WORKED — the search that found this row proved
    // it — so this indicts the tenant's own API rather than your access to it.
    forbidden:
        "This tenant's mail-processing endpoints refused the request. Opening the tenant worked, so the block is " +
        "inside that tenant rather than on your access to it.",
    unauthorised: "Your session has expired. Reload the page to sign in again.",
    failed: "This email could not be read. The request failed before the server answered.",
};

function Fact({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="mp-fact">
            <dt>{label}</dt>
            <dd>{children}</dd>
        </div>
    );
}

/**
 * One email: what it is, what state it is in, what can be done, how it got here.
 *
 * That order is the operator's decision order, and it is why the action bar
 * sits between the header card and the timeline rather than at the bottom:
 * eligibility is derived from the status directly above it.
 */
function EmailDetail({ emailRef, tenantKey, mailboxNames, tokenFor, onBack }: iEmailDetailProps) {
    const detail = useEmailDetail({ emailRef, tenantKey, tokenFor });

    const asOfClock = parseEmsDate(detail.detail?.asOf)?.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });

    const crumb = (
        <div className="mp-detail-crumb">
            <button
                type="button"
                className="mp-back"
                onClick={() => onBack(detail.acted)}
            >
                ← Back to worklist
            </button>
            <div className="mp-detail-asof">
                {detail.state === "ready" && asOfClock && <span className="mp-asof">as of {asOfClock}</span>}
                <button
                    type="button"
                    className="g-btn btn-default"
                    onClick={() => detail.refresh()}
                    disabled={detail.refreshing}
                >
                    {detail.refreshing ? "Refreshing…" : "Refresh"}
                </button>
            </div>
        </div>
    );

    if (detail.state === "loading") {
        return (
            <div className="mp-email-detail">
                {crumb}
                <Loading></Loading>
            </div>
        );
    }

    // THE DECISION IS `state`'S ALONE: a failed refresh keeps the previous
    // payload in state, and rendering it under a failure banner would read as
    // the current answer. The `|| !detail.detail` half decides nothing — the
    // hook sets "ready" and a payload together, and a 200 with no body falls
    // through to "failed" instead — but the compiler cannot know that, and
    // without it every dereference below would need its own guard. That
    // unreachability is also why the ternary names "failed": a fallback for a
    // combination the hook does not produce, not a fifth notice.
    if (detail.state !== "ready" || !detail.detail) {
        return (
            <div className="mp-email-detail">
                {crumb}
                <div className="mp-notice">
                    <p>{NOTICE[detail.state === "ready" ? "failed" : detail.state]}</p>
                </div>
            </div>
        );
    }

    const email = detail.detail;
    const faulted = email.lastFailureReason !== undefined;
    // Undefined when there is a real subject to render.
    const noSubject = subjectMarker(email.subject);

    return (
        <div className="mp-email-detail">
            {crumb}

            {/*
                Two columns on a wide screen, stacked on a narrow one — and the
                DOM order is the stacked order, deliberately.

                Identity, then state, then the action, then the history is the
                operator's decision order, and it is the order a screen reader
                and the tab key follow. The grid in styles.css only moves the
                history beside that column when there is room; it never
                reorders it. So `mp-detail-main` keeps the action bar directly
                under the status it is derived from, in both layouts.
            */}
            <div className="mp-detail-body">
                <div className="mp-detail-main">
                    <div className={faulted ? "mp-detail-card mp-detail-card-fault" : "mp-detail-card"}>
                        <div className="mp-detail-top">
                            <div>
                                {noSubject ? (
                                    <p
                                        className="mp-detail-subject mp-absent"
                                        title={noSubject.title}
                                    >
                                        {noSubject.label}
                                    </p>
                                ) : (
                                    <p className="mp-detail-subject">{email.subject}</p>
                                )}
                                <div className="mp-detail-parties">
                                    <span>
                                        <b>From</b>{" "}
                                        {optionalText(email.from, "Not recorded", "This message has no From header")}
                                    </span>
                                    <span>
                                        <b>To</b>{" "}
                                        {optionalText(email.to, "Not recorded", "This message has no To header")}
                                    </span>
                                    <span>
                                        <b>Mailbox</b>{" "}
                                        {email.mailboxID === undefined
                                            ? absent("Not recorded")
                                            : (mailboxNames.get(email.mailboxID) ?? `Mailbox ${email.mailboxID}`)}
                                    </span>
                                </div>
                            </div>
                            <div className="mp-detail-state">
                                {email.status ? (
                                    <span className={STATUS_PILL[email.status]}>{email.status}</span>
                                ) : (
                                    absent("This email reported no status")
                                )}
                                {/* Server-computed at read time (contract §2.3), and an
                                    input to inbound eligibility (spec §3.1). Shown, never
                                    offered as a control. */}
                                {email.stalled && (
                                    <span
                                        className="mp-detail-stalled"
                                        title="Stalled — server-computed, not derived here"
                                    >
                                        Stalled
                                    </span>
                                )}
                            </div>
                        </div>

                        <dl className="mp-facts">
                            <Fact label="Direction">{email.ref.direction}</Fact>
                            {isInboundDetail(email) ? (
                                <>
                                    <Fact label="Received">{clock(email.receivedAt)}</Fact>
                                    <Fact label="Attachments">{email.hasAttachments ? "Yes" : "No"}</Fact>
                                </>
                            ) : (
                                <>
                                    <Fact label="Origin">{email.origin ?? absent("Not recorded")}</Fact>
                                    <Fact label="Queued">{clock(email.queuedAt)}</Fact>
                                    <Fact label="Sent">{clock(email.sentAt)}</Fact>
                                    {/*
                                        Deliberately not `?? 0`: 0 attempts is a real
                                        report — built, not yet retried — while absent
                                        means the email did not report a count at all.
                                    */}
                                    <Fact label="Attempts">
                                        {email.attemptCount === undefined
                                            ? absent("This email did not report an attempt count")
                                            : email.attemptCount.toLocaleString()}
                                    </Fact>
                                </>
                            )}
                            <Fact label="Last activity">{clock(email.processingUpdatedAt)}</Fact>
                            {/* A value, not a link: this app has no enquiry view to link to. */}
                            <Fact label="Enquiry">
                                {email.enquiryID === undefined ? (
                                    absent("No enquiry was created for this email")
                                ) : (
                                    <span className="mp-email-id">{email.enquiryID}</span>
                                )}
                            </Fact>
                            <Fact label={email.ref.direction === "Inbound" ? "Data link ID" : "Message ID"}>
                                <span className="mp-email-id">{email.ref.id}</span>
                            </Fact>
                        </dl>

                        {email.lastFailureReason !== undefined && (
                            <div className="mp-detail-reason">
                                <b>Last failure reason</b>
                                {email.lastFailureReason}
                            </div>
                        )}

                        {/*
                            Keyed on the email so a failure message from one
                            email can never be shown against the next.
                        */}
                        <EmlDownload
                            key={`${email.ref.direction}-${email.ref.id}`}
                            emailRef={email.ref}
                            tenantKey={tenantKey}
                            tokenFor={tokenFor}
                        />
                    </div>

                    <ActionBar
                        direction={email.ref.direction}
                        status={email.status}
                        stalled={email.stalled}
                        sending={detail.sending}
                        result={detail.result}
                        onSubmit={reason => detail.submit(reason)}
                    />
                </div>

                <div className="mp-detail-history">
                    <div className="mp-tl-head">
                        <h2>Processing timeline</h2>
                        <span>oldest first</span>
                    </div>
                    <Timeline entries={email.timeline ?? []} />
                </div>
            </div>
        </div>
    );
}

export default EmailDetail;
